import { computed } from "vue"

/**
 * Translation key for the default search page title, used when the customer has
 * not configured an SEO title in the editor.
 */
export const SEARCH_PAGE_TITLE_KEY = "seo.search-page-title"

/**
 * Customisable SEO data for the search page, read from the jobshop settings
 * (`search.config.seo`, JET-831).
 *
 * The backend deliberately ships no canned defaults — every field is an empty
 * string until a customer fills it in — so unset values fall back to the
 * previous behaviour: the generic title, and no meta description / no visible
 * heading at all.
 *
 * `t` is passed in rather than resolved via `useI18n()` internally, so the
 * async-setup constraint stays visible at the call site (see AGENTS.md).
 */
export function useSearchPageSeo(t: ReturnType<typeof useI18n>["t"]) {
  const { data: jobShop } = useNuxtData<JobShop>("jobShopData")

  const seo = computed(() => jobShop.value?.search?.config?.seo)

  return {
    title: computed(() => seo.value?.title?.trim() || t(SEARCH_PAGE_TITLE_KEY)),
    // `undefined` (not "") so unhead omits the tag entirely instead of
    // rendering an empty meta description.
    description: computed(() => seo.value?.description?.trim() || undefined),
    h1: computed(() => seo.value?.h1?.trim() || ""),
  }
}
